import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  skipOnboardingCheck?: boolean;
  skipLicenseCheck?: boolean;
}

export const ProtectedRoute = ({ children, skipOnboardingCheck = false, skipLicenseCheck = false }: ProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const location = useLocation();

  // Wait for auth to finish loading
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (skipOnboardingCheck) {
    return <>{children}</>;
  }

  // Wait for the initial profile fetch to complete before rendering children.
  // Without this, a freshly-signed-in user can land on /dashboard with profile=null,
  // skipping the onboarding redirect and showing a blank screen.
  if (profileLoading || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isStudentExperience = profile?.role === 'student';

  // Phase 1 onboarding (goals/preferences): students only (includes "sales professionals" who sign up without an invite)
  if (
    profile &&
    isStudentExperience &&
    !profile.onboarding_completed_at &&
    location.pathname !== '/onboarding'
  ) {
    if (import.meta.env.DEV) console.log('[ProtectedRoute] Redirecting to /onboarding');
    return <Navigate to="/onboarding" replace />;
  }

  // Phase 2 profile completion: students only (NOT sales professionals)
  if (
    profile &&
    isStudentExperience &&
    profile.user_type !== 'professional' &&
    profile.onboarding_completed_at &&
    !profile.profile_completed_at &&
    location.pathname !== '/profile-setup'
  ) {
    if (import.meta.env.DEV) console.log('[ProtectedRoute] Redirecting to /profile-setup');
    return <Navigate to="/profile-setup" replace />;
  }

  return <>{children}</>;
};
